import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Check, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';

const insurancePricing = [
  { duration: "1 semaine", price: 45 },
  { duration: "2 semaines", price: 55 },
  { duration: "3 semaines", price: 65 },
  { duration: "1 mois", price: 75 },
  { duration: "2 mois", price: 95 },
  { duration: "3 mois", price: 135 }
];

const whyInsurance = [
  "Exigée par la plupart des pays pour l'obtention d'un visa",
  "Couvre les frais médicaux à l'étranger",
  "Assistance rapatriement en cas d'urgence",
  "Protection contre l'annulation de voyage"
];

export const InsurancePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '', destination: '', start_date: '', end_date: '',
    phone: '', email: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const msg = [
      `*Demande d'Assurance Voyage*`,
      ``,
      `Nom: ${formData.full_name}`,
      `Tél: ${formData.phone}`,
      `Email: ${formData.email}`,
      `Destination: ${formData.destination}`,
      `Date début: ${formData.start_date}`,
      `Date fin: ${formData.end_date}`
    ].join('\n');

    window.open(getWhatsAppLink(WHATSAPP_NUMBER, msg), '_blank');
    setFormData({ full_name: '', destination: '', start_date: '', end_date: '', phone: '', email: '' });
  };

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Header */}
      <section className="pt-6 pb-4 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Assurance Voyage</h1>
              <p className="text-[#94A3B8] text-xs">Voyagez en toute sérénité</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-xl mx-auto flex flex-col gap-6">
            {/* Info */}
            <div className="space-y-6">
              <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                <h3 className="text-xs font-semibold text-white mb-3">Pourquoi une assurance?</h3>
                <ul className="space-y-2">
                  {whyInsurance.map((reason, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[#94A3B8] text-xs">{reason}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="card-luxury overflow-hidden" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 px-4 py-2">
                  <h3 className="text-xs font-semibold text-white">Grille Tarifaire</h3>
                </div>
                <table className="w-full" data-testid="insurance-pricing-table">
                  <tbody>
                    {insurancePricing.map((item, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-4 py-2 text-[#94A3B8] text-xs">{item.duration}</td>
                        <td className="px-4 py-2 text-right font-semibold text-[#D4AF37] text-xs">{item.price}$</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </div>

            {/* Form */}
            <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h3 className="text-xs font-semibold text-white mb-3">Demander une Assurance</h3>
              <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="insurance-form">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required placeholder="Votre nom" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-name" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-phone" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-email" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Destination *</Label>
                  <Input value={formData.destination} onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    required placeholder="Pays de destination" className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-destination" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Date Début *</Label>
                    <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-start" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Date Fin *</Label>
                    <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      required className="bg-white/5 border-white/10 text-white text-xs h-8" data-testid="input-end" />
                  </div>
                </div>
                <button type="submit" className="w-full btn-gold py-2.5 text-xs flex items-center justify-center gap-2" data-testid="submit-btn">
                  <MessageCircle className="w-3.5 h-3.5" />
                  Envoyer via WhatsApp
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;
